import { useEffect, useRef, useState, useCallback } from 'react';
import { CampaignFormData, Location } from '../types';

interface UseUnsavedChangesProps {
  mode: 'create' | 'draft' | 'edit';
  campaignName: string;
  formData: CampaignFormData;
  selectedLocations: Location[];
  duration: number;
  isLoading: boolean;
  autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error' | 'validating' | 'retrying';
}

export const useUnsavedChanges = ({
  mode,
  campaignName,
  formData,
  selectedLocations,
  duration,
  isLoading,
  autoSaveStatus
}: UseUnsavedChangesProps) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Store the original data when loaded
  const originalDataRef = useRef<string>('');
  const dataLoadedRef = useRef(false);

  // Only track changes in edit mode
  const shouldTrack = mode === 'edit';

  // Capture original data once loaded
  useEffect(() => {
    if (!isLoading && !dataLoadedRef.current && shouldTrack) {
      const originalData = JSON.stringify({
        campaignName,
        formData,
        selectedLocations,
        duration
      });
      originalDataRef.current = originalData;
      dataLoadedRef.current = true;
    }
  }, [isLoading, campaignName, formData, selectedLocations, duration, shouldTrack]);

  // When auto-save completes, update original data reference
  useEffect(() => {
    if (autoSaveStatus === 'saved' && shouldTrack) {
      const currentData = JSON.stringify({
        campaignName,
        formData,
        selectedLocations,
        duration
      });
      originalDataRef.current = currentData;
      setHasUnsavedChanges(false);
    }
  }, [autoSaveStatus, campaignName, formData, selectedLocations, duration, shouldTrack]);

  // Detect changes by comparing current state with original
  useEffect(() => {
    if (!dataLoadedRef.current || !shouldTrack || autoSaveStatus === 'saving') return;

    const currentData = JSON.stringify({
      campaignName,
      formData,
      selectedLocations,
      duration
    });

    const hasChanges = currentData !== originalDataRef.current;
    setHasUnsavedChanges(hasChanges);
  }, [campaignName, formData, selectedLocations, duration, shouldTrack, autoSaveStatus]);

  // Handle browser back/refresh with beforeunload
  useEffect(() => {
    if (!shouldTrack) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, shouldTrack]);

  const handleConfirmLeave = useCallback(() => {
    setHasUnsavedChanges(false);
  }, []);

  const handleCancelLeave = useCallback(() => {
    // No-op since we're not blocking in-app navigation
  }, []);

  const markAsSaved = useCallback(() => {
    const currentData = JSON.stringify({
      campaignName,
      formData,
      selectedLocations,
      duration
    });
    originalDataRef.current = currentData;
    setHasUnsavedChanges(false);
  }, [campaignName, formData, selectedLocations, duration]);

  return {
    hasUnsavedChanges,
    showDialog: false, // Not showing dialog for in-app navigation
    handleConfirmLeave,
    handleCancelLeave,
    markAsSaved
  };
};
